import React, { useEffect } from "react";
import { EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiShowFor } from "@elastic/eui";
import _ from "lodash";
import moment from "moment";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import TripDetails from "./TripDetails";
import TripCheckList from "./TripCheckList";
import TripDropoff from "./TripDropoff";
import TripActivity from "./TripActivity";
import {
    driverSelector,
    truckSelector,
    ordersSelector,
    pointsAttachmentsSelector,
    suppliersSelector,
    truckTypeSelector,
    branchesSelector,
    tripTypesSelector,
    projectsSelector,
} from "../../slice";
import {
    mapResourceObjectToArray,
    extractResourceAttributes,
} from "../../../../utils/jsonApiUtils";
import TripNotes from "./TripNotes";
import tripStatus, { isTerminationState } from "../../constants/tripStates";
import dropdownFieldsOptions from "../../configs/dropdownFields";
import DrivingDetails from "./DrivingDetails";
import tripDetailsFields from "../../configs/tripDetailsFields";
import numberField from "../../configs/numberField";
import PickupDetails from "./PickupDetails";
import TripFieldsConfirmationCheckList from "./TripFieldsConfirmationCheckList";
import CommentsView from "../../../shared/Comments/view";
import TripAssignees from "./TripAssignees";
import trackAnalytics from "../../../../trackAnalytics";
import { commentsSelectors } from "../../../shared/Comments/commentSlice";

export default function SummaryView({
    canCurrentUserEditTripsAfterCompleted,
    trip,
    tripPoints,
    confirmedTrips,
    admins,
    onDropoffsChange,
    onDropoffDelete,
    onPointFieldChange,
    onTripDataChange,
    onSupplierChange,
    onSupplierContractTypeChange,
    onTagCounterChange,
    onMakeTripReturnable,
    onConfirmingTripField,
}) {
    const driver = extractResourceAttributes(useSelector(driverSelector()));
    const truck = extractResourceAttributes(useSelector(truckSelector()));
    const truckType = extractResourceAttributes(useSelector(truckTypeSelector()));
    const tripType = extractResourceAttributes(useSelector(tripTypesSelector()));
    const order = extractResourceAttributes(useSelector(ordersSelector()));
    const supplier = extractResourceAttributes(useSelector(suppliersSelector()));
    const project = extractResourceAttributes(useSelector(projectsSelector()));
    const comments = useSelector(commentsSelectors()); // for tracking
    const tripDropOffs = tripPoints.filter((point) => point.pointType === "dropoff");
    const tripPickup = tripPoints.find((point) => point.pointType === "pickup") || {};
    const pickupLocation = extractResourceAttributes(tripPickup.locationAttributes) || {};
    const attachments = mapResourceObjectToArray(useSelector(pointsAttachmentsSelector())) || [];
    const branch = extractResourceAttributes(useSelector(branchesSelector()));
    const pickupAttachments = attachmentsByPoint(tripPickup.id);
    const pickupDuration =
        tripPickup.departureTime && tripPickup.arrivalTime
            ? moment(tripPickup.departureTime).diff(moment(tripPickup.arrivalTime)) / 1000
            : null;
    const confirmedTripsWithAdmin = confirmedTrips.map((ct) => {
        const admin = _.find(admins, { id: `${ct.confirmedById}` });
        return admin && admin.email && { confirmedByEmail: admin.email, ...ct };
    });

    const tripCheckList = {
        isDropoffEntered: !!tripDropOffs.length,
        isDriverAssigned: !!trip.driverId,
        isTruckAssigned: !!trip.truckId,
        isPickupDocumentsUploaded: tripPickup.id && attachmentsByPoint(tripPickup.id).length,
        isDropoffDocumentsUploaded:
            !!tripDropOffs &&
            !!tripDropOffs.length &&
            tripDropOffs.every((dropoff) => attachmentsByPoint(dropoff.id).length),
    };
    const confirmedFieldsCheckList = {
        isDriverConfirmed: !!trip.driverIdConfirmed,
        isTruckConfirmed: !!trip.truckIdConfirmed,
        isSupplierConfirmed: !!trip.supplierIdConfirmed,
    };

    // TODO: Move to selector
    function attachmentsByPoint(pointId) {
        return attachments.filter((attachment) => attachment.attachedItemId === +pointId);
    }

    const isTripStatusPending = trip.status === tripStatus.PENDING;
    const intl = useIntl();

    const canEditPointFields =
        canCurrentUserEditTripsAfterCompleted || !isTerminationState(trip.status);
    const isEditable = canCurrentUserEditTripsAfterCompleted || isTripStatusPending;
    // The below variable to be use on fields that
    // can always be edited from any type of user on any state of the trip!
    const alwaysEditableFields = true;

    const dropoffsWithoutReturnable = tripDropOffs.filter(
        (tp) => tp?.locationId !== tripPickup?.locationId
    );
    const tripPointsLocationCategoryNames = _.uniqBy(
        dropoffsWithoutReturnable,
        "locationCategoryName"
    )
        .map((tp) => tp.locationCategoryName)
        .join("/");

    // for analytics tracking
    useEffect(() => {
        if (attachments && tripDropOffs && comments && trip?.id && trip?.status) {
            trackAnalytics(`/trips/${trip.id}`, {
                tripDropOffs: tripDropOffs.length,
                attachments: attachments.length > 0,
                NoOfAttachments: attachments.length,
                notes: !!trip.notes,
                comments: comments.comments.length > 0,
                tripStatus: trip.status,
                geofence: trip.geofence,
                platform: trip.platformType,
                source: trip.source,
                Tags: trip.tags.length,
            });
        }
    }, [trip.status]);

    return (
        <>
            <EuiFlexGroup
                gutterSize="none"
                justifyContent="flexStart"
            >
                <EuiFlexItem
                    grow={3}
                    className="trip-details-container"
                >
                    <DrivingDetails
                        supplierOptions={dropdownFieldsOptions.supplier}
                        supplierName={supplier && supplier.name}
                        isSupplierConfirmed={trip.supplierIdConfirmed}
                        isSupplierEditable={
                            (canCurrentUserEditTripsAfterCompleted ||
                                (!trip.supplierIdConfirmed && isTripStatusPending)) &&
                            supplier
                        }
                        onSupplierChange={onSupplierChange}
                        supplierContractTypeOptions={dropdownFieldsOptions.supplierContractType}
                        supplierContractTypeName={trip.supplierContractType}
                        isSupplierContractTypeEditable={
                            canCurrentUserEditTripsAfterCompleted || isTripStatusPending
                        }
                        onSupplierContractTypeChange={onSupplierContractTypeChange}
                        driverOptions={dropdownFieldsOptions.drivers}
                        driverName={driver.name}
                        isDriverConfirmed={trip.driverIdConfirmed}
                        isDriverEditable={
                            (canCurrentUserEditTripsAfterCompleted ||
                                (!trip.driverIdConfirmed && isTripStatusPending)) &&
                            supplier.id
                        }
                        onDriverChange={onTripDataChange}
                        truckOptions={dropdownFieldsOptions.trucks}
                        truckPlateNumber={truck.plateNumber}
                        isTruckConfirmed={trip.truckIdConfirmed}
                        isTruckEditable={
                            (canCurrentUserEditTripsAfterCompleted ||
                                (!trip.truckIdConfirmed && isTripStatusPending)) &&
                            supplier.id
                        }
                        onTruckChange={onTripDataChange}
                        truckType={truckType}
                        onConfirmingField={onConfirmingTripField}
                        supplierId={supplier && supplier.id}
                        clientNotes={order?.clientNotes}
                        orderId={order?.id}
                        organizationId={order?.organizationId}
                    />
                    <EuiHorizontalRule margin="none" />

                    <TripDetails
                        clientName={order.organizationName}
                        organizationId={order?.organizationId}
                        branchName={branch.nameAr || branch.nameEn}
                        branchOptions={dropdownFieldsOptions.branches}
                        onBranchIdChange={onTripDataChange}
                        contractTypeOptions={{
                            ...tripDetailsFields.contractType,
                            data: [
                                {
                                    value: "monthly",
                                    text: intl.formatMessage({ id: "contractType.monthly" }),
                                },
                                {
                                    value: "on_demand",
                                    text: intl.formatMessage({ id: "contractType.on-demand" }),
                                },
                                {
                                    value: "daily_rental",
                                    text: intl.formatMessage({ id: "contractType.daily-rental" }),
                                },
                            ],
                        }}
                        contractType={trip.contractType}
                        onContractTypeChange={onTripDataChange}
                        // isContractTypeEditable={trip.contractType === null} // uncomment if you want it to be uneditable
                        isContractTypeEditable={isEditable} // set 'isEditable' if u need it to be editable link in case the contract type is N/A
                        projectName={project.name}
                        tripPickupTime={trip.pickupTime}
                        tripPickupTimeOptions={tripDetailsFields.pickupTime}
                        onTripPickupTimeChange={onTripDataChange}
                        isPickupTimeTripEditable={false}
                        tripSourceOptions={tripDetailsFields.source}
                        tripSource={trip.source}
                        onTripSourceChange={onTripDataChange}
                        isTripSourceEditable={isEditable}
                        tripReturnableOptions={tripDetailsFields.returnable}
                        isReturnable={trip.returnable}
                        onTripReturnableChange={(_data, successCallBack) =>
                            onMakeTripReturnable(_data, successCallBack)
                        }
                        isReturnableEditable={!trip.returnable && isEditable}
                        TripGeofencingOptions={tripDetailsFields.geofence}
                        onTripGeofenceChange={onTripDataChange}
                        isGeofencing={!!trip.geofence}
                        isTripGeofencingEditable={isEditable}
                        isAutoEnding={!!trip.autoEnding}
                        onAutoEndingChange={onTripDataChange}
                        isAutoEndingEditable={isEditable}
                        autoEndingOptions={tripDetailsFields.autoEnding}
                        TripCollectionShipmentAmountsOptions={
                            tripDetailsFields.collectionShipmentAmounts
                        }
                        isTripCollectionShipmentAmounts={!!trip.collectingNumberOffloadedUnits}
                        onTripCollectionShipmentAmountsChange={onTripDataChange}
                        isTripCollectionShipmentAmountsEditable={isEditable}
                        googleDistance={
                            _.isNil(trip.googleDistance) ? "N/A" : `${trip.googleDistance} KMs`
                        }
                        calculatedDistance={
                            _.isNil(trip.calculatedDistance)
                                ? "N/A"
                                : `${trip.calculatedDistance} KMs`
                        }
                        manualDistanceOptions={numberField.manualDistance}
                        manualDistance={trip.manualDistance}
                        onManualDistanceChange={onTripDataChange}
                        isManualDistanceEditable={alwaysEditableFields}
                        helpersCountOptions={numberField.helpersCount}
                        helpersCount={trip.helpersCount}
                        onHelpersCountChange={onTripDataChange}
                        isHelpersCountEditable={isEditable}
                        scalesCountOptions={numberField.scalesCount}
                        scalesCount={trip.scalesCount}
                        onScalesCountChange={onTripDataChange}
                        isScalesCountEditable={alwaysEditableFields}
                        tollStationGatesCountOptions={numberField.tollStationGatesCount}
                        tollStationGatesCount={trip.tollStationGatesCount}
                        onTollStationGatesCountChange={onTripDataChange}
                        isTollStationGatesCountEditable={alwaysEditableFields}
                        preTripDistanceOptions={numberField.preTripDistance}
                        preTripDistance={trip.preTripDistance}
                        onPreTripDistanceChange={onTripDataChange}
                        isPreTripDistanceEditable={isEditable}
                        tags={trip.tags}
                        tagsCount={trip.tagsCount}
                        onTagCountChange={(requestedCount, tag, successCallBack) =>
                            onTagCounterChange(requestedCount, tag, successCallBack)
                        }
                        tagCountOptions={numberField.tagCount}
                        isTagsCountEditable={isEditable}
                        tripTypeOptions={dropdownFieldsOptions.tripTypes}
                        tripTypeName={tripPointsLocationCategoryNames}
                        onTripTypeChange={onTripDataChange}
                        isTripTypeEditable={false}
                        tripCostOptions={numberField.cost}
                        tripCost={trip.cost}
                        onTripCostChange={onTripDataChange}
                        isTripCostEditable
                        appVersion={trip.clientVersion}
                        isTripStatusPending={isTripStatusPending}
                    />
                    <EuiHorizontalRule margin="none" />

                    <PickupDetails
                        arrivalTimeOptions={tripDetailsFields.pickupArrivalTime}
                        arrivalTime={tripPickup.arrivalTime || null}
                        onArrivalTimeChange={(pickupData, successCallBack) =>
                            onPointFieldChange(pickupData, tripPickup.id, successCallBack)
                        }
                        isArrivalTimeEditable={isEditable}
                        departureTimeOptions={tripDetailsFields.pickupDepartureTime}
                        departureTime={tripPickup.departureTime || null}
                        onDepartureTimeChange={(pickupData, successCallBack) =>
                            onPointFieldChange(pickupData, tripPickup.id, successCallBack)
                        }
                        isDepartureTimeEditable={isEditable}
                        pickupDuration={pickupDuration}
                        attachmentsOptions={tripDetailsFields.pickupAttachments}
                        attachments={pickupAttachments}
                        onAttachmentsChange={(pickupData, successCallBack) =>
                            onPointFieldChange(pickupData, tripPickup.id, successCallBack)
                        }
                        isAttachmentEditable={alwaysEditableFields}
                        pickupName={pickupLocation.name || pickupLocation.address}
                        podNumberOptions={tripDetailsFields.pickupPODNumber}
                        podNumber={tripPickup.podNumber}
                        onPodNumberChange={(pickupData, successCallBack) =>
                            onPointFieldChange(pickupData, tripPickup.id, successCallBack)
                        }
                        isPodNumberEditable
                        shipmentAmountOptions={tripDetailsFields.shipmentNumberOfUnits}
                        shipmentAmount={
                            _.isNumber(tripPickup.shipmentNumberOfUnits)
                                ? tripPickup.shipmentNumberOfUnits
                                : null
                        }
                        onShipmentAmountChange={(pickupData, successCallBack) =>
                            onPointFieldChange(pickupData, tripPickup.id, successCallBack)
                        }
                        isShipmentAmountEditable={isEditable}
                        shipmentUnitOptions={{
                            ...tripDetailsFields.shipmentUnit,
                            data: [
                                {
                                    value: "cases",
                                    text: intl.formatMessage({ id: "shipmentUnit.cases" }),
                                },
                                {
                                    value: "ton",
                                    text: intl.formatMessage({ id: "shipmentUnit.ton" }),
                                },
                                {
                                    value: "kg",
                                    text: intl.formatMessage({ id: "shipmentUnit.kg" }),
                                },
                                {
                                    value: "pallet",
                                    text: intl.formatMessage({ id: "shipmentUnit.pallet" }),
                                },
                                {
                                    value: "meter",
                                    text: intl.formatMessage({ id: "shipmentUnit.meter" }),
                                },
                            ],
                        }}
                        shipmentUnit={tripPickup.shipmentUnit}
                        onShipmentUnitChange={(pickupData, successCallBack) =>
                            onPointFieldChange(pickupData, tripPickup.id, successCallBack)
                        }
                        isShipmentUnitEditable={isEditable}
                    />
                </EuiFlexItem>

                <EuiShowFor sizes={["xs", "s"]}>
                    <EuiHorizontalRule margin="none" />
                </EuiShowFor>

                <EuiFlexItem grow={1}>
                    <TripAssignees tripId={trip.id} />
                    <EuiHorizontalRule margin="none" />
                    <TripCheckList checkList={tripCheckList} />
                    <EuiHorizontalRule margin="none" />
                    <TripFieldsConfirmationCheckList checkList={confirmedFieldsCheckList} />
                </EuiFlexItem>
            </EuiFlexGroup>

            <EuiHorizontalRule margin="none" />

            <EuiFlexGroup
                gutterSize="none"
                justifyContent="flexStart"
            >
                <EuiFlexItem>
                    <TripDropoff
                        pickupPoint={tripPickup}
                        tripDropoffs={tripDropOffs}
                        attachments={attachments}
                        tripId={trip.id}
                        organizationId={trip.organizationId}
                        tripType={tripType}
                        canAppendPoints={canEditPointFields}
                        isFieldsEditable={canEditPointFields}
                        alwaysEditableFields={alwaysEditableFields}
                        onDropoffsChange={onDropoffsChange}
                        onDropOffFieldChange={onPointFieldChange}
                        onDropoffDelete={onDropoffDelete}
                    />
                </EuiFlexItem>
            </EuiFlexGroup>

            <EuiHorizontalRule margin="none" />

            <EuiFlexGroup
                gutterSize="none"
                justifyContent="flexStart"
            >
                <TripNotes
                    defaultValue={trip.notes}
                    onChange={onTripDataChange}
                />
            </EuiFlexGroup>

            <EuiHorizontalRule margin="none" />
            <EuiFlexGroup
                gutterSize="none"
                justifyContent="flexStart"
            >
                <EuiFlexItem>
                    <CommentsView
                        tripId={trip.id}
                        commentableType="Trip"
                    />
                </EuiFlexItem>
            </EuiFlexGroup>
            <EuiHorizontalRule margin="none" />

            <EuiFlexGroup
                gutterSize="none"
                justifyContent="flexStart"
            >
                <EuiFlexItem>
                    <TripActivity
                        activities={trip.activity || []}
                        pickupPoint={tripPickup}
                        tripPoints={tripPoints}
                        confirmedTripsWithAdmin={confirmedTripsWithAdmin}
                        driverName={driver.name}
                    />
                </EuiFlexItem>
            </EuiFlexGroup>
        </>
    );
}
